import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Loader from "../../components/Loader";
import { getRequest, putWithFormData } from "../../helpers/api.helper";
import { UPLOAD_URL } from "../../utils/constants";

const Step11Edit = () => {
  const [images, setImages] = useState(Array(5).fill(null));
  const [processingSlots, setProcessingSlots] = useState<boolean[]>(
    Array(5).fill(false),
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isRefreshingGallery, setIsRefreshingGallery] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const { profile } = location.state || {};

  // 🧩 Compress image helper
  const compressImage = (file: any, maxSizeKB = 500) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const img: any = new Image();
        img.src = reader.result;
        img.onload = () => {
          const canvas = document.createElement("canvas");
          const ctx: any = canvas.getContext("2d");
          const maxWidth = 800;
          const maxHeight = 800;

          let width = img.width;
          let height = img.height;

          if (width > height && width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          } else if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }

          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob((blob) => {
            if (!blob) {
              reject(new Error("Unable to prepare image preview"));
              return;
            }
            resolve(blob);
          }, "image/jpeg", 0.8);
        };
      };
      reader.onerror = () => reject(new Error("Unable to read image file"));
    });
  };

  // 📤 Upload
  const handleImageUpload = async (e: any, index: any) => {
    if (isLoading || isRefreshingGallery) return;
    const file = e.target.files[0];
    if (!file) return;

    setProcessingSlots((prev) => {
      const updated = [...prev];
      updated[index] = true;
      return updated;
    });

    try {
      const compressedFile = await compressImage(file);
      setImages((prev) => {
        const updated = [...prev];
        updated[index] = compressedFile;
        return updated;
      });
    } catch (error) {
      console.error("Error preparing image preview:", error);
      alert("❌ Failed to prepare image preview.");
    } finally {
      setProcessingSlots((prev) => {
        const updated = [...prev];
        updated[index] = false;
        return updated;
      });
      e.target.value = "";
    }
  };

  // ❌ Remove image
  const removeImage = (index: any) => {
    if (isLoading || isRefreshingGallery) return;
    setImages((prev) => {
      const updated = [...prev];
      updated[index] = null;
      return updated;
    });
  };

  // 💾 Submit new uploads
  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const formData = new FormData();
      const existingImages = images.filter((img) => typeof img === "string");
      const newImages = images.filter(
        (img) => img instanceof File || img instanceof Blob
      );
      newImages.forEach((file, i) => {
        formData.append("gallery", file, `image_${i}.jpeg`);
      });
      formData.append("existing_images", JSON.stringify(existingImages));
      const response = await putWithFormData(
        `/bureau/profile/${profile._id}`,
        formData
      );
      if (response.success) {
        setIsRefreshingGallery(true);
        try {
          const refreshed = await getRequest(
            `/bureau/profile/${profile?.martialId || id}`
          );
          if (refreshed.success) {
            const galleryList = refreshed.data?.gallery || [];
            const filledSlots = Array(5).fill(null);
            galleryList.slice(0, 5).forEach((filename: any, i: any) => {
              filledSlots[i] = filename;
            });
            setImages(filledSlots);
          }
        } catch (refreshError) {
          console.error("Error refreshing gallery after update:", refreshError);
        } finally {
          setIsRefreshingGallery(false);
        }
        alert("✅ Gallery updated successfully!");
        // navigate(-1);
      } else {
        alert("❌ Failed to update gallery.");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("Server error while updating gallery.");
    } finally {
      setIsLoading(false);
    }
  };

  // 🧠 Fetch existing gallery
  useEffect(() => {
    const fetchGallery = async () => {
      if (!profile?.martialId && !id) return;
      setIsLoadingData(true);

      try {
        const res = await getRequest(
          `/bureau/profile/${profile?.martialId || id}`
        );
        if (res.success && res.data?.gallery) {
          const galleryList = res.data.gallery || [];
          const filledSlots = Array(5).fill(null);
          galleryList.slice(0, 5).forEach((filename: any, i: any) => {
            filledSlots[i] = filename;
          });
          setImages(filledSlots);
        }
      } catch (err) {
        console.error("Error loading gallery:", err);
      } finally {
        setIsLoadingData(false);
      }
    };

    fetchGallery();
  }, [id, profile]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (isLoadingData) return <Loader />;

  const isBusy = isLoading || isRefreshingGallery;

  return (
    <>
      {/* 🔺 Header */}
      <div className="w-full z-50 bg-red-600 fixed top-0 mb-12 p-2 px-3 flex items-center justify-center">
        <div className="flex items-center gap-2">
          <button className="flex items-center" onClick={() => navigate(-1)}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <h1 className="text-2xl font-bold text-center text-white py-3">
            Edit Gallery
          </h1>
        </div>
        <h5 className="font-semibold text-white">Update Images</h5>
      </div>

      {/* 🖼️ Gallery Section */}
      <div className="profile-edit-page max-w-4xl mx-auto p-6 mt-20">
        {isBusy && (
          <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center">
            <div className="bg-white rounded-2xl px-8 py-6 shadow-xl flex flex-col items-center gap-3">
              <Loader />
              <p className="text-sm font-medium text-gray-700">
                {isRefreshingGallery
                  ? "Refreshing gallery..."
                  : "Updating gallery..."}
              </p>
            </div>
          </div>
        )}
        <h2 className="text-2xl font-bold mb-4 text-center">
          Edit Gallery Images
        </h2>
        <p className="text-gray-600 text-center mb-6">
          You can upload up to 5 images. Click on an image to replace or use +
          to add.
        </p>

        <div className="grid grid-cols-4 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {images.map((image, index) => (
            <div
              key={index}
              className="relative border rounded w-20 h-20 flex items-center justify-center bg-gray-100"
            >
              {processingSlots[index] ? (
                <div className="absolute inset-0 z-10 flex flex-col items-center justify-center rounded bg-white/85">
                  <div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
                  <span className="mt-1 text-[10px] font-medium text-gray-600">
                    Loading...
                  </span>
                </div>
              ) : null}
              {image ? (
                <>
                  <img
                    src={
                      typeof image === "string"
                        ? `${UPLOAD_URL}/users/gallery/${image}`
                        : URL.createObjectURL(image)
                    }
                    alt={`Gallery ${index + 1}`}
                    className="w-full h-full object-cover cursor-pointer rounded"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
                    <div className="flex space-x-1">
                      <input
                        type="file"
                        accept="image/*"
                        className="absolute inset-0 opacity-0 cursor-pointer"
                        onChange={(e) => handleImageUpload(e, index)}
                        disabled={isBusy || processingSlots[index]}
                      />
                      <button
                        onClick={() => removeImage(index)}
                        className="bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600 disabled:opacity-50"
                        disabled={isBusy || processingSlots[index]}
                      >
                        ×
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <input
                    type="file"
                    accept="image/*"
                    className="absolute inset-0 opacity-0 cursor-pointer"
                    onChange={(e) => handleImageUpload(e, index)}
                    disabled={isBusy || processingSlots[index]}
                  />
                  <span className="text-gray-500 text-xl">+</span>
                </>
              )}
            </div>
          ))}
        </div>

        {/* 🔘 Action Buttons */}
        <div className="flex gap-4 mt-6">
          <button
            className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 flex-1 disabled:opacity-60"
            onClick={() => navigate(-1)}
            disabled={isBusy}
          >
            Cancel
          </button>
          <button
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 flex-1 disabled:opacity-60"
            onClick={handleSubmit}
            disabled={isBusy}
          >
            {isRefreshingGallery
              ? "Refreshing..."
              : isLoading
                ? "Updating..."
                : "Update Gallery"}
          </button>
        </div>
      </div>
    </>
  );
};

export default Step11Edit;
